import { createContext, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import {
  AuthApiError,
  loginTalent,
  redeemClaim,
  revokePlatformSession,
  rotatePlatformSession,
  signupTalent,
  type AuthLoginPrincipal,
  type PlatformSession,
} from '@/features/auth/api'
import {
  clearSessionTokens,
  getRefreshToken,
  refreshAccessToken,
  registerRefreshHandler,
  setAccessToken,
  setRefreshToken,
} from '@/services/session'

const REFRESH_EARLY_SECONDS = 60

export interface AuthUser {
  userId: string
  grants: AuthLoginPrincipal['grants']
}

export interface AuthContextValue {
  user: AuthUser | null
  isInitializing: boolean
  isAuthenticated: boolean
  error: string | null
  signIn(email: string, password: string): Promise<void>
  signUp(email: string, password: string, name?: { first?: string; last?: string }): Promise<void>
  claimProfile(token: string, input: { password?: string; name?: { first?: string; last?: string } }): Promise<void>
  logout(): Promise<void>
  clearError(): void
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  return <RealAuthProvider>{children}</RealAuthProvider>
}

function RealAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isInitializing, setIsInitializing] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const refreshTimerRef = useRef<number | null>(null)
  const mountedRef = useRef(true)
  const bootstrapAttemptRef = useRef(false)

  const cancelRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current !== null) {
      window.clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
  }, [])

  const clearLocalSession = useCallback(() => {
    cancelRefreshTimer()
    clearSessionTokens()
  }, [cancelRefreshTimer])

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken()
    clearLocalSession()
    setUser(null)
    setError(null)

    if (refreshToken) {
      try {
        await revokePlatformSession(refreshToken)
      } catch {
        // Local sign-out must still finish when the gateway is temporarily unavailable.
      }
    }
  }, [clearLocalSession])

  const applySession = useCallback(
    (session: PlatformSession) => {
      if (!session.accessToken || !session.refreshToken || session.expiresIn <= 0) {
        throw new Error('Authentication service returned an invalid session')
      }

      setAccessToken(session.accessToken)
      setRefreshToken(session.refreshToken)
      cancelRefreshTimer()

      const delay = Math.max(1, session.expiresIn - REFRESH_EARLY_SECONDS) * 1_000
      refreshTimerRef.current = window.setTimeout(() => {
        void refreshAccessToken().catch(() => undefined)
      }, delay)
    },
    [cancelRefreshTimer],
  )

  const refreshPlatformSession = useCallback(async () => {
    const refreshToken = getRefreshToken()
    if (!refreshToken) throw new Error('No refresh token')

    try {
      const session = await rotatePlatformSession(refreshToken)
      applySession(session)
      return session.accessToken
    } catch (refreshError) {
      if (refreshError instanceof AuthApiError && [401, 403].includes(refreshError.status)) {
        await logout()
      }
      throw refreshError
    }
  }, [applySession, logout])

  useEffect(() => {
    registerRefreshHandler(refreshPlatformSession)
    return () => registerRefreshHandler(null)
  }, [refreshPlatformSession])

  /**
   * Bootstrap on mount: there is no Firebase session to observe, so "am I logged in" is answered
   * by whether a refresh token survived (sessionStorage) — the same source `refreshPlatformSession`
   * uses at every subsequent rotation, not a separate identity check.
   */
  useEffect(() => {
    mountedRef.current = true
    const existingRefreshToken = getRefreshToken()

    if (!existingRefreshToken) {
      setIsInitializing(false)
      return () => {
        mountedRef.current = false
        cancelRefreshTimer()
      }
    }

    if (bootstrapAttemptRef.current) {
      // StrictMode's dev-only double-invoke (mount → cleanup → mount) reaches this effect twice
      // for one real mount. Both would read the SAME refresh token and rotate it concurrently —
      // the loser's 401 then clears the session the winner just established. One ref, one
      // attempt; the second invocation leaves `isInitializing` alone and lets the first settle it.
      return () => {
        mountedRef.current = false
        cancelRefreshTimer()
      }
    }
    bootstrapAttemptRef.current = true

    void rotatePlatformSession(existingRefreshToken)
      .then((session) => {
        if (!mountedRef.current) return
        applySession(session)
        setUser(toAuthUser(session.principal))
      })
      .catch(() => {
        clearLocalSession()
        // A stale or revoked refresh token; the login screen is the correct next step, not an error.
      })
      .finally(() => {
        if (mountedRef.current) setIsInitializing(false)
      })

    return () => {
      mountedRef.current = false
      cancelRefreshTimer()
    }
  }, [applySession, cancelRefreshTimer, clearLocalSession])

  const signIn = useCallback(
    async (email: string, password: string) => {
      setError(null)
      clearLocalSession()

      try {
        const session = await loginTalent(email, password)
        applySession(session)
        setUser(toAuthUser(session.principal))
      } catch (signInError) {
        const message = authErrorMessage(signInError)
        setError(message)
        throw new Error(message)
      }
    },
    [applySession, clearLocalSession],
  )

  const signUp = useCallback(
    async (email: string, password: string, name?: { first?: string; last?: string }) => {
      setError(null)
      clearLocalSession()

      try {
        const session = await signupTalent(email, password, name)
        applySession(session)
        setUser(toAuthUser(session.principal))
      } catch (signUpError) {
        const message = authErrorMessage(signUpError)
        setError(message)
        throw new Error(message)
      }
    },
    [applySession, clearLocalSession],
  )

  const claimProfile = useCallback(
    async (token: string, input: { password?: string; name?: { first?: string; last?: string } }) => {
      setError(null)
      clearLocalSession()

      try {
        const session = await redeemClaim(token, input)
        applySession(session)
        setUser(toAuthUser(session.principal))
      } catch (claimError) {
        const message = authErrorMessage(claimError)
        setError(message)
        throw new Error(message)
      }
    },
    [applySession, clearLocalSession],
  )

  return (
    <AuthContext.Provider
      value={{
        user,
        isInitializing,
        isAuthenticated: !isInitializing && user !== null,
        error,
        signIn,
        signUp,
        claimProfile,
        logout,
        clearError: () => setError(null),
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

function toAuthUser(principal: AuthLoginPrincipal): AuthUser {
  return {
    userId: principal.userId,
    grants: principal.grants,
  }
}

function authErrorMessage(error: unknown) {
  if (error instanceof AuthApiError) return error.message
  return error instanceof Error ? error.message : 'Unable to sign in. Please try again.'
}
